// hooks/useSEO.js
import { useEffect } from "react";

const q = (sel) => document.head.querySelector(sel);

function removeAll(selector) {
  document.head.querySelectorAll(selector).forEach((el) => el.remove());
}

function upsertMeta(by, key, value) {
  if (value == null || value === "") return;
  const selector = by === "name" ? `meta[name="${key}"]` : `meta[property="${key}"]`;
  let el = q(selector);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(by, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", value);
}

function setMultipleProperty(key, values = []) {
  // remove all existing then add one per value
  removeAll(`meta[property="${key}"]`);
  values.filter(Boolean).forEach((v) => {
    const el = document.createElement("meta");
    el.setAttribute("property", key);
    el.setAttribute("content", v);
    document.head.appendChild(el);
  });
}

function upsertLink(rel, href) {
  if (!href) return;
  let el = q(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

export default function useSEO(seo = null) {
  useEffect(() => {
    if (!seo || typeof document === "undefined") return;

    // Prefer the generator’s keys, fall back to alternates
    const finalTitle =
      seo.title || seo.metaTitle || seo.ogTitle || "";
    const finalDesc =
      seo.description || seo.metaDescription || seo.ogDescription || "";
    const canonical =
      seo.canonical || seo.canonicalUrl || "";
    const ogImage = seo.ogImage || "";
    const ogType = seo.ogType || "website";
    const ogUrl  = seo.ogUrl || canonical || "";
    const twitterImage = seo.twitterImage || ogImage || "";
    const twitterCard = seo.twitterCard || "summary_large_image";
    const twitterSite = seo.twitterSite || "";
    const robots = seo.robots || "";
    const keywords = seo.keywords || "";
    const author = seo.author || "";

    // Title
    if (finalTitle) document.title = finalTitle;

    // Canonical
    upsertLink("canonical", canonical);

    // Basics
    if (finalDesc) upsertMeta("name", "description", finalDesc);
    if (keywords) upsertMeta("name", "keywords", keywords);
    if (robots) upsertMeta("name", "robots", robots);
    if (author) upsertMeta("name", "author", author);

    // Open Graph
    upsertMeta("property", "og:title", finalTitle);
    upsertMeta("property", "og:description", finalDesc);
    upsertMeta("property", "og:image", ogImage);
    upsertMeta("property", "og:type", ogType);
    if (ogUrl) upsertMeta("property", "og:url", ogUrl);

    // Article namespace (when provided)
    if (seo.articlePublishedTime) upsertMeta("property", "article:published_time", seo.articlePublishedTime);
    if (seo.articleModifiedTime)  upsertMeta("property", "article:modified_time",  seo.articleModifiedTime);
    if (seo.articleSection)       upsertMeta("property", "article:section",       seo.articleSection);
    if (Array.isArray(seo.articleTags)) setMultipleProperty("article:tag", seo.articleTags);

    // Twitter
    upsertMeta("name", "twitter:card", twitterCard);
    if (twitterSite) upsertMeta("name", "twitter:site", twitterSite);
    upsertMeta("name", "twitter:title", finalTitle);
    upsertMeta("name", "twitter:description", finalDesc);
    if (twitterImage) upsertMeta("name", "twitter:image", twitterImage);

    // JSON-LD (deduped)
    if (seo.jsonLd) {
      let script = q('script[type="application/ld+json"][data-seo="1"]');
      if (!script) {
        script = document.createElement("script");
        script.type = "application/ld+json";
        script.setAttribute("data-seo", "1");
        document.head.appendChild(script);
      }
      script.text = JSON.stringify(seo.jsonLd);
    }
  }, [seo]);
}
